import React from 'react';
import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "inflation-types-causes-wpi-cpi-india-notes",
  title: "Inflation in India: Types, Causes, WPI vs CPI, Phillips Curve & RBI Inflation Targeting",
  description: "Learn about inflation types, causes, measurement (WPI vs CPI), Phillips curve, and RBI's inflation targeting in India.",
  pubDate: "2025-08-21",
  authorSlug: "dhairya-madaan",
  category: "subject-explainers",
  tags: ["Macroeconomics", "Inflation", "Indian Economy", "RBI"],
  readMinutes: 14,
  heroEyebrow: "Macroeconomics Notes"
};

export const Body = () => {
  return (
    <article>
      <h2>What is Inflation?</h2>
      <p>Inflation is a sustained increase in the general price level of goods and services in an economy over a period of time. It is measured as an annual percentage increase. As inflation rises, every rupee you own buys a smaller percentage of a good or service.</p>

      <h2>Types of Inflation</h2>
      <ul>
        <li><strong>Demand-Pull Inflation:</strong> Occurs when aggregate demand outpaces aggregate supply. "Too much money chasing too few goods."</li>
        <li><strong>Cost-Push Inflation:</strong> Driven by an increase in the cost of production (e.g., higher wages, raw material costs like oil), which decreases aggregate supply.</li>
        <li><strong>Structural Inflation:</strong> Caused by structural bottlenecks like poor infrastructure, agricultural shortfalls, or rigidities in the supply chain, very common in developing nations like India.</li>
        <li><strong>Built-In Inflation:</strong> Linked to adaptive expectations. Workers demand higher wages to keep up with living costs, prompting firms to raise prices.</li>
      </ul>

      <h2>Measurement: WPI vs CPI in India</h2>
      <p>India uses various indices to measure inflation. The Wholesale Price Index (WPI) measures price changes at the wholesale level, heavily weighted towards manufactured goods and primary articles. It does not include services.</p>
      <p>The Consumer Price Index (CPI) measures price changes from the perspective of a retail buyer. It heavily weights food and beverages and includes services (housing, education, health).</p>
      <p>The RBI formally uses CPI (Combined) for its inflation targeting framework.</p>

      <h3>Headline vs Core Inflation</h3>
      <p>Headline inflation is the raw inflation figure reported through CPI. Core inflation excludes volatile components like food and energy prices to reveal underlying inflation trends.</p>

      <h2>The Phillips Curve</h2>
      <p>The original Phillips Curve posited an inverse trade-off between inflation and unemployment. Policymakers could choose lower unemployment at the cost of higher inflation. However, the expectations-augmented Phillips Curve introduced by Friedman and Phelps argued that in the long run, there is no trade-off. The economy gravitates towards the Non-Accelerating Inflation Rate of Unemployment (NAIRU), represented by a vertical long-run Phillips Curve. Stagflation (high inflation + high unemployment) dismantled the original theory.</p>

      <h2>RBI's Inflation Targeting Framework</h2>
      <p>Since 2016, India operates under a Flexible Inflation Targeting (FIT) framework. The statutory target is 4% CPI inflation, with a tolerance band of +/- 2% (i.e., 2% to 6%). The Monetary Policy Committee (MPC), a 6-member body headed by the RBI Governor, sets the benchmark policy repo rate to achieve this target.</p>

      <h2>Anti-Inflationary Policies</h2>
      <p>1. Monetary Policy: RBI raises repo rates, increases CRR, or sells government securities (Open Market Operations) to suck out excess liquidity.</p>
      <p>2. Fiscal Policy: Government can reduce expenditure or increase taxes to dampen demand.</p>
      
      <h2>Solved Numerical Problems</h2>
      <h3>Problem 1: Price Index Calculation</h3>
      <p>Base Year Basket Cost = 5000. Current Year Basket Cost = 6200. Find the Price Index.</p>
      <p>Solution: Price Index = (Current Cost / Base Cost) * 100 = (6200 / 5000) * 100 = 124.</p>
      <p>Inflation Rate = 24%.</p>
    </article>
  );
};


